import { z } from 'zod'

import { contrastRatio, AA_THRESHOLD, type TextSize } from '#theme/contrast'

/**
 * The tenant theme contract. **Tokens, never CSS** (stack §13.1).
 *
 * Raw CSS from a tenant is an injection and exfiltration vector: `background-image: url(...)`
 * leaks that a given user viewed a given page to an attacker-controlled host, and `content:`
 * can alter the text the user reads. A tenant asking for "just a little custom CSS" is asking
 * for the one thing this design refuses.
 *
 * The refusal is structural rather than a promise, because the vocabulary is **closed**: every
 * key is enumerated, every value is a validated hex color or a member of an allowlist, and
 * the schema is strict. Nothing reaches the generated stylesheet that was not one of those —
 * which is also why the CSS writer has no escaping to do, and therefore no escaping to get
 * wrong.
 *
 * Shared by the API (which validates on write and generates the stylesheet) and the admin
 * console's editor (which validates as you type). One schema, so the editor cannot offer
 * something the server will refuse.
 */

/** The shadcn/ui variable set. Adding one is a deliberate change to the contract. */
export const COLOR_TOKENS = [
  '--background',
  '--foreground',
  '--card',
  '--card-foreground',
  '--popover',
  '--popover-foreground',
  '--primary',
  '--primary-foreground',
  '--secondary',
  '--secondary-foreground',
  '--muted',
  '--muted-foreground',
  '--accent',
  '--accent-foreground',
  '--destructive',
  '--destructive-foreground',
  '--border',
  '--input',
  '--ring',
] as const

export type ColorToken = (typeof COLOR_TOKENS)[number]

/**
 * Fonts come from an allowlist, for the same reason CSS does not come at all.
 *
 * An arbitrary font URL is the same leak with a different property: the browser fetches it
 * from a host the tenant chose, and that request says who is reading which page and when.
 */
export const FONT_ALLOWLIST = [
  'Inter',
  'Geist',
  'Figtree',
  'Source Sans 3',
  'IBM Plex Sans',
  'Plus Jakarta Sans',
  'system-ui',
] as const

/** A real fallback stack, so a webfont that fails to load does not leave a serif nobody chose. */
const FALLBACK_STACK =
  "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"

/**
 * The `--font-sans` value for an allowlisted family.
 *
 * Here rather than in `css.ts`, which imports `node:crypto` for the ETag: the editor's preview
 * needs the same value in the browser, and a second copy there is a preview that renders one
 * stack while the published stylesheet ships another.
 */
export function fontStack(fontFamily: (typeof FONT_ALLOWLIST)[number]): string {
  return fontFamily === 'system-ui' ? FALLBACK_STACK : `'${fontFamily}', ${FALLBACK_STACK}`
}

/** `#rgb` or `#rrggbb`, and nothing else. No `rgb()`, no `var()`, no bare keywords. */
const hexColor = z
  .string()
  .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'Must be a hex color such as #1d4ed8.')

/**
 * One `hexColor` per declared token, built from the list rather than typed out.
 *
 * A literal object would be nineteen lines that have to stay in step with `COLOR_TOKENS`, and
 * the day they diverge is the day a token silently validates as anything. The loop assigns
 * every key before the value is used, so the mapped type is a post-condition rather than a
 * claim — which is why it needs no assertion.
 */
const paletteShape: Record<ColorToken, typeof hexColor> = Object.create(null)
for (const token of COLOR_TOKENS) paletteShape[token] = hexColor

/** `strict`: an unknown token is refused rather than dropped. */
const paletteSchema = z.strictObject(paletteShape)

export const themeSchema = z.strictObject({
  light: paletteSchema,
  /**
   * Required, not optional. Editing one mode must not silently desync the other — an operator
   * who tunes light and never opens dark ships a dark mode nobody designed.
   */
  dark: paletteSchema,
  /** Bounded, and a plain `rem` value: `calc()` is an expression, and expressions are code. */
  radius: z
    .string()
    .regex(/^(?:0|[0-2](?:\.\d{1,3})?)rem$/, 'Must be a rem value between 0 and 2, e.g. 0.5rem.'),
  fontFamily: z.enum(FONT_ALLOWLIST),
})

export type Theme = z.infer<typeof themeSchema>
export type Palette = z.infer<typeof paletteSchema>
export type ThemeMode = 'light' | 'dark'

/**
 * Pairs where **body copy** is rendered. A failure here **blocks publish**.
 *
 * These are the reading surfaces: the page itself, cards, popovers, and muted secondary text.
 * Somebody reads paragraphs against every one of them, so 4.5:1 is the right bar and shipping
 * below it makes the product unusable rather than merely ugly.
 */
export const BLOCKING_PAIRS: readonly (readonly [ColorToken, ColorToken])[] = [
  ['--background', '--foreground'],
  ['--card', '--card-foreground'],
  ['--popover', '--popover-foreground'],
  ['--muted', '--muted-foreground'],
]

/**
 * Pairs used for **control labels**. A failure here warns.
 *
 * A button label is not body copy — it is short, isolated and usually set heavier — so 3:1 is
 * the honest bar. Holding these to 4.5 would refuse designs that are genuinely fine, and a
 * validator that refuses fine designs is a validator somebody switches off.
 */
export const WARNING_PAIRS: readonly (readonly [ColorToken, ColorToken])[] = [
  ['--primary', '--primary-foreground'],
  ['--secondary', '--secondary-foreground'],
  ['--accent', '--accent-foreground'],
  ['--destructive', '--destructive-foreground'],
]

export type ContrastIssue = {
  mode: ThemeMode
  pair: string
  background: string
  foreground: string
  ratio: number
  required: number
  size: TextSize
}

export type ContrastReport = {
  /** Publishing is refused while this is non-empty. */
  blocking: ContrastIssue[]
  warnings: ContrastIssue[]
}

function check(
  theme: Theme,
  pairs: readonly (readonly [ColorToken, ColorToken])[],
  size: TextSize,
): ContrastIssue[] {
  const issues: ContrastIssue[] = []

  for (const mode of ['light', 'dark'] as const) {
    const palette = theme[mode]
    for (const [background, foreground] of pairs) {
      const ratio = contrastRatio(palette[foreground], palette[background])
      if (ratio >= AA_THRESHOLD[size]) continue

      issues.push({
        mode,
        pair: `${background}/${foreground}`,
        background: palette[background],
        foreground: palette[foreground],
        // The ACTUAL ratio travels with the issue. An operator told "4.48, needs 4.5"
        // darkens by one step; one told "insufficient contrast" opens a picker and guesses.
        ratio: Math.round(ratio * 100) / 100,
        required: AA_THRESHOLD[size],
        size,
      })
    }
  }

  return issues
}

export function validateThemeContrast(theme: Theme): ContrastReport {
  return {
    blocking: check(theme, BLOCKING_PAIRS, 'body'),
    warnings: check(theme, WARNING_PAIRS, 'large'),
  }
}

/**
 * The theme every new tenant starts from.
 *
 * It passes its own validator, which is not a coincidence worth leaving to chance: a default
 * that could not pass would be the first reason anybody had to turn the validator off.
 */
export function defaultTheme(): Theme {
  return {
    light: {
      '--background': '#ffffff',
      '--foreground': '#0a0a0a',
      '--card': '#ffffff',
      '--card-foreground': '#0a0a0a',
      '--popover': '#ffffff',
      '--popover-foreground': '#0a0a0a',
      '--primary': '#1d4ed8',
      '--primary-foreground': '#ffffff',
      '--secondary': '#f4f4f5',
      '--secondary-foreground': '#18181b',
      '--muted': '#f4f4f5',
      '--muted-foreground': '#52525b',
      '--accent': '#f4f4f5',
      '--accent-foreground': '#18181b',
      '--destructive': '#b91c1c',
      '--destructive-foreground': '#ffffff',
      '--border': '#e4e4e7',
      '--input': '#e4e4e7',
      '--ring': '#1d4ed8',
    },
    dark: {
      '--background': '#09090b',
      '--foreground': '#fafafa',
      '--card': '#09090b',
      '--card-foreground': '#fafafa',
      '--popover': '#09090b',
      '--popover-foreground': '#fafafa',
      '--primary': '#60a5fa',
      '--primary-foreground': '#0a0a0a',
      '--secondary': '#27272a',
      '--secondary-foreground': '#fafafa',
      '--muted': '#27272a',
      '--muted-foreground': '#a1a1aa',
      '--accent': '#27272a',
      '--accent-foreground': '#fafafa',
      '--destructive': '#f87171',
      '--destructive-foreground': '#0a0a0a',
      '--border': '#27272a',
      '--input': '#27272a',
      '--ring': '#60a5fa',
    },
    radius: '0.5rem',
    /**
     * **`Plus Jakarta Sans`, not `Inter`** (frontend plan §11, D3).
     *
     * This is what a *newly provisioned* edition starts with. Existing `tenant_branding` rows are
     * stored themes and are untouched by it, so there is no migration — a tenant who chose Inter
     * keeps Inter, and one who never chose keeps whatever they were provisioned with.
     *
     * The choice only became a real one when `packages/ui/src/styles/fonts.css` started loading
     * the allowlisted families. Before that no font file was ever fetched and every tenant
     * rendered in the system stack whatever this said.
     */
    fontFamily: 'Plus Jakarta Sans',
  }
}
